import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { CheckIcon } from "@heroicons/react/24/outline"
import { store } from "aldoo-ra/Store"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import { getHighlightEntry, resolveHighlight } from "./helpers"

export default function PlanCard({ plan, billing, isPopular, storeData, texts, activePid, features }) {
  const productId = typeof plan.product === "string" ? plan.product : (plan.product?.item || plan.product?._id)
  const product = storeData.products?.[productId]
  const priceId = product?.price?._id || product?.price || product?.meta?.prices?.monthly
  const storePrice = storeData.prices?.[priceId]

  const getModelByInterval = (interval) => {
    if (!storePrice?.price_model) return null
    return storePrice.price_model.find((m) => m.phases?.[0]?.interval === interval) ?? null
  }

  const monthlyModel = getModelByInterval("month")
  const annualModel = getModelByInterval("year")

  const monthlyAmount = monthlyModel?.amount ?? null
  const annualAmount = annualModel?.amount ?? null
  const annualPerMonth = annualAmount != null ? Math.round((annualAmount / 12) * 100) / 100 : null
  const displayAmount = billing === "annual" && annualPerMonth != null ? annualPerMonth : monthlyAmount
  const isFree = plan.id === "free"

  const handleBuy = () => {
    const selectedModel = billing === "annual" ? (annualModel || monthlyModel) : (monthlyModel || annualModel)
    if (!selectedModel || !storePrice) return

    const webEntry = selectedModel.real_product_id?.find((r) => {
      const platform = typeof r.platform === "object" ? r.platform?.value : r.platform
      return platform === "Web"
    })
    const storeId = webEntry?.id || null
    const currency = selectedModel.currency || "USD"

    store.addToCart({
      product: {
        pid: product?.pid,
        product_id: storePrice._id,
        store_id: storeId,
        name: product?.name || plan.name,
        description: product?.description,
        currency,
        price_model: [{ ...selectedModel, primary: true }],
        duration: billing === "annual" ? texts?.perYear : texts?.perMonth,
        thumbnail: product?.thumbnail,
      },
      proceedToCart: true,
    })
  }

  return (
    <div
      className={`relative flex flex-col h-full rounded-3xl border p-6 transition-shadow hover:shadow-lg bg-gradient-to-br from-paper/40 via-paper to-paper/70 ${
        isPopular ? "border-2 border-primary shadow-md" : "border-bordered"
      }`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap">
          {texts?.mostPopular}
        </div>
      )}

      {product?.thumbnail && (
        <div className="w-16 h-16 rounded-[10px] overflow-hidden shrink-0 mb-3 mx-auto">
          <img
            src={cmsUrl(product.thumbnail)}
            alt={plan.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <Typography
        variant="h3"
        className="text-text text-center mb-1"
      >
        {plan.name}
      </Typography>

      {plan.tagline && (
        <Typography
          variant="txt1"
          className="block min-h-[40px] my-4"
        >
          {plan.tagline}
        </Typography>
      )}

      <div className="min-h-[90px] mb-4">
        {isFree ? (
          <Typography component="span" variant="h2">
            {texts?.free}
          </Typography>
        ) : displayAmount != null ? (
          <>
            <Typography component="span" variant="h2">
              ${displayAmount}
            </Typography>

            <span className="text-text_secondary text-sm ml-1">
              {texts?.perMonth}
            </span>

            {billing === "annual" && annualAmount != null && (
              <Typography
                variant="txt2_dim"
                className="block mt-1"
              >
                {texts?.billedAnnually} · ${annualAmount}{" "}
                {texts?.perYear}
              </Typography>
            )}

            {billing === "monthly" && annualPerMonth != null && (
              <Typography
                variant="txt2_dim"
                className="block mt-1"
              >
                ${annualPerMonth} {texts?.perMonth} {texts?.if} {texts?.billedAnnually}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography component="span" variant="h2">
              {plan.price_monthly}
            </Typography>

            <span className="text-text_secondary text-sm">
              {texts?.perMonth}
            </span>
          </>
        )}
      </div>

      {activePid && product?.pid === activePid ? (
        <Button
          variant="primary"
          className="w-full mb-6 disabled:opacity-100 cursor-default bg-success hover:bg-success"
          disabled
        >
          {texts?.currentPlan}
        </Button>
      ) : (
        <Button
          variant="primary"
          className="w-full mb-6"
          onClick={handleBuy}
        >
          {activePid && !isFree ? (texts?.changePlan) : (plan.cta_label || texts?.getStarted)}
        </Button>
      )}

      {plan.highlights && (() => {
        const items = Array.isArray(plan.highlights)
          ? plan.highlights
          : Object.values(plan.highlights)
        return items.length > 0 && (
          <ul className="space-y-2">
            {items.map((item, i) => {
              const { name, value } = getHighlightEntry(item, plan, product, features)
              if (!name) return null
              return (
                <li key={i} className="flex items-start gap-2 text-sm text-text">
                  <CheckIcon className="w-4 h-4 text-success stroke-[3] shrink-0 mt-0.5" />
                  <span>
                    <span className="text-text_secondary">{resolveHighlight(name, product)}</span>
                    {value && (
                      <span className="font-semibold text-text">{" "}{resolveHighlight(value, product)}</span>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        )
      })()}
    </div>
  )
}